import { IS_DEMO } from 'config/constants';
import { jwtDecode } from 'jwt-decode';
import { createContext, Dispatch, useContext } from 'react';
import * as types from 'types';

export const appState: types.AppState = {
  messages: [],
  auth: { token: undefined, user: undefined, roles: [], attributes: undefined },
};

export const appStateFake: types.AppState = {
  messages: [],
  auth: {
    token: '',
    user: {
      family_name: '',
      given_name: '',
      emails: [],
      email: 'dummy@shape.com',
      scp: '',
      oid: '',
      exp: 0,
      iat: 0,
      name: 'Dummy User',
    },
    roles: [],
    attributes: undefined,
  },
};

export const ADD_MESSAGE = 'ADD_MESSAGE';
export const REMOVE_MESSAGE = 'REMOVE_MESSAGE';
export const CLEAR_MESSAGE = 'CLEAR_MESSAGE';
export const ADD_AUTH_TOKEN = 'ADD_AUTH_TOKEN';
export const REMOVE_AUTH_TOKEN = 'REMOVE_AUTH_TOKEN';
export const SET_USER_ROLES = 'SET_USER_ROLES';
export const SET_USER_ATTRIBUTES = 'SET_USER_ATTRIBUTES';

type ADD_MESSAGE = typeof ADD_MESSAGE;
type REMOVE_MESSAGE = typeof REMOVE_MESSAGE;
type CLEAR_MESSAGE = typeof CLEAR_MESSAGE;
type ADD_AUTH_TOKEN = typeof ADD_AUTH_TOKEN;
type REMOVE_AUTH_TOKEN = typeof REMOVE_AUTH_TOKEN;

type UserRolesAction = {
  type: typeof SET_USER_ROLES;
  data: string[];
};

type UserAttributesAction = {
  type: typeof SET_USER_ATTRIBUTES;
  data: { [key: string]: unknown };
};

type MessageAction =
  | {
      type: ADD_MESSAGE;
      text: string | JSX.Element;
      id: string;
      messageType: 'error' | 'warning' | 'info' | 'success';
      onClick?: () => void;
    }
  | {
      type: REMOVE_MESSAGE;
      id: string;
    }
  | {
      type: CLEAR_MESSAGE;
    };

type AuthAction =
  | { type: ADD_AUTH_TOKEN; token: string }
  | { type: REMOVE_AUTH_TOKEN }
  | { type: typeof SET_USER_ROLES; data: string[] }
  | { type: typeof SET_USER_ATTRIBUTES; data: { [key: string]: unknown } };

const authReducer = (
  state: types.Auth,
  action: MessageAction | AuthAction | UserAttributesAction | UserRolesAction,
): types.Auth => {
  switch (action.type) {
    case ADD_AUTH_TOKEN: {
      if (action.token === state.token) {
        return state;
      }
      const user = jwtDecode(action.token || '') as types.User & { emails?: string[] };
      return {
        ...state,
        token: action.token,
        user: { ...user, email: user.email ?? user.emails?.[0] },
      };
    }
    case REMOVE_AUTH_TOKEN:
      return {
        token: undefined,
        user: undefined,
        attributes: undefined,
        roles: [],
      };
    case SET_USER_ATTRIBUTES:
      return {
        ...state,
        attributes: action.data,
      };
    case SET_USER_ROLES:
      if (action.data === state.roles) {
        return state;
      }
      return {
        ...state,
        roles: action.data,
      };
    default:
      return state;
  }
};

const messagesReducer = (
  state: types.MessageInterface[],
  action: MessageAction | AuthAction,
): types.MessageInterface[] => {
  switch (action.type) {
    case ADD_MESSAGE:
      return [
        ...state,
        {
          text: action.text,
          id: action.id || new Date().getTime().toString(),
          messageType: action.messageType,
          onClick: action.onClick,
        },
      ];
    case REMOVE_MESSAGE:
      return state.filter((item: types.MessageInterface) => item.id !== action.id);
    case CLEAR_MESSAGE:
      return [];
    default:
      return state;
  }
};

export const appReducer = (
  { messages, auth }: types.AppState,
  action: MessageAction | AuthAction,
) => ({
  messages: messagesReducer(messages, action),
  auth: authReducer(auth, action),
});

export const AppContext = createContext<{
  state: types.AppState;
  dispatch: Dispatch<MessageAction | AuthAction>;
}>({
  state: IS_DEMO ? appStateFake : appState,
  dispatch: () => null,
});
export const useHasWritePermission = () => {
  const context = useContext(AppContext);

  return Boolean(context.state.auth.roles?.includes('READ_WRITE'));
};

export const useGetUserPermissions = () => {
  const context = useContext(AppContext);

  return {
    hasWrite: Boolean(context.state.auth.roles?.includes('READ_WRITE')),
    isAdmin: context.state.auth.roles?.includes('ADMIN'),
  };
};
